import { useState, useEffect, useCallback } from 'react';
import { Copy, Share2, Gift, Users, CheckCircle, MessageCircle, Mail, Facebook, Trophy } from 'lucide-react';
import { referralAPI } from '../../services/referralApi';
import toast from 'react-hot-toast';

interface ReferralStats {
  referralCode: string;
  referralLink: string;
  referralCount: number;
  points: number;
  isEligibleForFreePro: boolean;
}

export default function ReferralPage() {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchStats = useCallback(async (isInitial = false) => {
    try {
      const res = await referralAPI.getStats();
      setStats(res.data.data);
      setError(false);
    } catch {
      if (isInitial) setError(true);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(true);
    const interval = setInterval(() => fetchStats(false), 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [fetchStats]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleShare = async (platform: 'whatsapp' | 'email' | 'facebook') => {
    if (!stats) return;

    // OG preview URL — WhatsApp crawls this for the rich card, then redirects to register
    const ogUrl = `https://mocktestnation.com/register?ref=${(stats.referralCode)}`;
    const absoluteOgUrl = `${ogUrl}`;

    const msgByPlatform: Record<typeof platform, string> = {
      whatsapp: ` *Join Mock Test Nation — JEE Preparation Platform*\n\nI've been using it for full-length mock tests, AI-powered analytics, and personalised study plans. It's genuinely helped my prep.\n\nSign up free using my referral link \n${absoluteOgUrl}`,
      email: `Hey,\n\nI wanted to share Mock Test Nation with you — it's a JEE prep platform with full-length mock tests, AI analytics, and personalised study plans.\n\nSign up free using my referral link:\n${absoluteOgUrl}\n\nGood luck with your prep!`,
      facebook: `I've been preparing for JEE on Mock Test Nation and it's been great. Join me using my referral link: ${absoluteOgUrl}`,
    };
    const msg = msgByPlatform[platform];
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(msg)}`,
      email: `mailto:?subject=Join Mock Test Nation — JEE Prep Platform&body=${encodeURIComponent(msg)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(absoluteOgUrl)}&quote=${encodeURIComponent(msg)}`,
    };
    window.open(urls[platform], '_blank');
    referralAPI.logShare(platform).catch(() => {});
  };

  const handleClaim = async () => {
    setClaiming(true);
    try {
      await referralAPI.claimReward();
      toast.success('🎉 Free Pro subscription activated!');
      fetchStats();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to claim reward');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!stats && error) {
    return (
      <div className="text-center py-12 text-secondary-500">
        Failed to load referral data. Please refresh.
      </div>
    );
  }

  if (!stats) return null;
  const progress = Math.min((stats.referralCount / 20) * 100, 100);

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Gift className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-secondary-900">Refer & Earn</h1>
        <p className="text-secondary-600 mt-1">Invite friends and earn rewards for every successful referral</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-secondary-100">
          <div className="text-3xl font-bold text-primary-600">{stats.referralCount}</div>
          <div className="text-sm text-secondary-500 mt-1">Referrals</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-secondary-100">
          <div className="text-3xl font-bold text-purple-600">{stats.points}</div>
          <div className="text-sm text-secondary-500 mt-1">Points</div>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-secondary-100">
          <div className="text-3xl font-bold text-green-600">{20 - stats.referralCount > 0 ? 20 - stats.referralCount : 0}</div>
          <div className="text-sm text-secondary-500 mt-1">Until Free Pro</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-secondary-100">
        <div className="flex justify-between text-sm text-secondary-600 mb-2">
          <span className="flex items-center gap-1"><Trophy className="h-4 w-4 text-yellow-500" /> Progress to Free Pro</span>
          <span>{stats.referralCount} / 20</span>
        </div>
        <div className="w-full bg-secondary-100 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-primary-500 to-purple-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-xs text-secondary-400 mt-2">Reach 20 successful referrals to unlock a free 1-month Pro subscription</p>
      </div>

      {/* Claim reward */}
      {stats.isEligibleForFreePro && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-5 text-center">
          <p className="font-semibold text-yellow-800 mb-3">🎉 You've earned a free Pro month!</p>
          <button
            onClick={handleClaim}
            disabled={claiming}
            className="btn-primary px-8 py-2 disabled:opacity-60"
          >
            {claiming ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Claiming...
              </span>
            ) : (
              'Claim Free Pro Month'
            )}
          </button>
        </div>
      )}

      {/* Referral code & link */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-secondary-100 space-y-4">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Your Referral Code</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-secondary-50 border border-secondary-200 rounded-lg px-4 py-2 font-mono text-lg text-center tracking-widest">
              {stats.referralCode}
            </div>
            <button
              onClick={() => copyToClipboard(stats.referralCode)}
              className="p-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              {copied ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Referral Link</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-secondary-50 border border-secondary-200 rounded-lg px-3 py-2 text-sm truncate text-secondary-600">
              {stats.referralLink}
            </div>
            <button
              onClick={() => copyToClipboard(stats.referralLink)}
              className="p-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg transition-colors"
            >
              {copied ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Share buttons */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-secondary-100">
        <p className="text-sm font-medium text-secondary-700 mb-3">Share via</p>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => handleShare('whatsapp')}
            className="flex items-center justify-center gap-2 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors"
          >
            <MessageCircle className="h-5 w-5" />
            WhatsApp
          </button>
          <button
            onClick={() => handleShare('email')}
            className="flex items-center justify-center gap-2 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors"
          >
            <Mail className="h-5 w-5" />
            Email
          </button>
          <button
            onClick={() => handleShare('facebook')}
            className="flex items-center justify-center gap-2 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors"
          >
            <Facebook className="h-5 w-5" />
            Facebook
          </button>
        </div>
        <button
          onClick={() => copyToClipboard(stats.referralLink)}
          className="w-full mt-3 flex items-center justify-center gap-2 py-3 border border-secondary-200 hover:bg-secondary-50 rounded-xl font-medium text-secondary-700 transition-colors"
        >
          <Share2 className="h-4 w-4" />
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>

      {/* How it works */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-secondary-100">
        <h3 className="font-semibold text-secondary-900 mb-3 flex items-center gap-2">
          <Users className="h-5 w-5 text-primary-600" /> How it works
        </h3>
        <ol className="space-y-2 text-sm text-secondary-600">
          <li className="flex gap-3"><span className="font-bold text-primary-600">1.</span> Share your referral link with friends</li>
          <li className="flex gap-3"><span className="font-bold text-primary-600">2.</span> They sign up and verify their email</li>
          <li className="flex gap-3"><span className="font-bold text-primary-600">3.</span> You earn 10 points per successful referral</li>
          <li className="flex gap-3"><span className="font-bold text-primary-600">4.</span> Reach 20 referrals to claim a free Pro month</li>
        </ol>
      </div>
    </div>
  );
}
